import type { IPointerController, IPointerHandler } from '@/components/viewPort/PointerController';
import type { PointerSyntheticEvent } from '@/components/viewPort/PointerSyntheticEvent';
import type { SyntheticEvent } from '@/components/viewPort/SyntheticEvent';

export abstract class PointerHandlerAdapter<T> implements IPointerHandler {
  protected _controller?: T;

  protected _parent?: IPointerController;

  public setParent(ctrl: IPointerController): void {
    this._parent = ctrl;
  }

  public adapt(controller: T): void {
    this._controller = controller;
  }

  public reset(): void {
    // no-op
  }
}

export interface PointerHandlerAdaptersSet<T> {
  mouseAdapter: PointerHandlerAdapter<T>;
  touchAdapter: PointerHandlerAdapter<T>;
}

export class PointerHandlerComposite<T> implements IPointerHandler {
  private readonly _adaptersSet: PointerHandlerAdaptersSet<T>;

  private _lastEventType?: 'mouse' | 'touch';

  public constructor(controller: T, adapters: PointerHandlerAdaptersSet<T>) {
    this._adaptersSet = adapters;
    this._adaptersSet.mouseAdapter.adapt(controller);
    this._adaptersSet.touchAdapter.adapt(controller);
  }

  public setParent(parent: IPointerController): void {
    this._adaptersSet.mouseAdapter.setParent(parent);
    this._adaptersSet.touchAdapter.setParent(parent);
  }

  public onDblClick(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onDblClick');
  }

  public onPointerDown(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onPointerDown');
  }

  public onPointerUp(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onPointerUp');
  }

  public onPointerMove(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onPointerMove');
  }

  public onPointerLeave(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onPointerLeave');
  }

  public onPointerEnter(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onPointerEnter');
  }

  public onPointerCancel(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onPointerCancel');
  }

  public onContextCommand(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onContextCommand');
  }

  public onFixedClick(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onFixedClick');
  }

  public onLongPress(event: PointerSyntheticEvent): void {
    this._delegateEvent(event, 'onLongPress');
  }

  public onWheel(event: SyntheticEvent<WheelEvent>): void {
    const handler = this._adaptersSet.mouseAdapter as IPointerHandler;
    if (handler.onWheel) {
      handler.onWheel(event);
    }
  }

  private _delegateEvent<K extends keyof Omit<IPointerHandler, 'setParent' | 'onWheel'>>(
    event: PointerSyntheticEvent,
    methodName: K
  ): void {
    const eventType = this._isTouchEvent(event) ? 'touch' : 'mouse';
    if (this._lastEventType && this._lastEventType !== eventType) {
      const previousAdapter = this._adaptersSet[this._lastEventType === 'touch' ? 'touchAdapter' : 'mouseAdapter'];
      previousAdapter.reset();
    }
    const adapter = this._adaptersSet[eventType === 'touch' ? 'touchAdapter' : 'mouseAdapter'];
    const method = (adapter as IPointerHandler)[methodName];
    if (method) {
      method.call(adapter, event);
      this._lastEventType = eventType;
    }
  }

  private _isTouchEvent(event: PointerSyntheticEvent): boolean {
    return event.originalEvent.pointerType === 'touch';
  }
}
